import { useCallback, useRef, useState } from 'react';
import { extend, isEmpty } from 'lodash';
import { useInfrastructureErrorHandler } from './UseInfrastructureErrorHandler';
import { useEntityIdParam } from '../../AdminLinkUtils';
import {
    DESCRIPTION_FIELD,
    DISCOVERY_PLC,
    ID_MAPPING,
    INSERT_TIME,
    IPADDRESS_FIELD,
    MANUAL_NAME,
    MONITORING_PLC,
    MONITORING_PLC_NAME,
    NAME_FIELD,
    OWNER_PLC,
    PLCNAME_FIELD,
    PORT_FIELD,
    SUBTYPE_FIELD,
    UISTATE_FIELD,
    VCENTER_HOSTNAME_FIELD,
} from '../InfrastructureConstants';
import { ConfirmMsg, getSubtypeSet } from '../InfrastructureHelper';
import { getSystemTime } from '../../../../stores/SystemTimeStore';
import { createBaseDetailObject } from '../utils/InfrastructureDefaults';

const DEFAULT_PORT = 443;

const DEFAULT_VALUES = {
    [NAME_FIELD]: '',
    [IPADDRESS_FIELD]: '',
    [PLCNAME_FIELD]: '',
    [UISTATE_FIELD]: '',
    [SUBTYPE_FIELD]: '',
    [DESCRIPTION_FIELD]: '',
    [PORT_FIELD]: DEFAULT_PORT,
};

const VCENTER_NEW_DETAIL_OBJECT = createBaseDetailObject({
    ipAddress: '127.0.0.1',
    deviceType: 'vcenter',
    subType: 'vcenter',
});

const VCENTER_NEW_VIRTUALIZATION = {
    virtualization: {
        collector: {},
        status: 'active',
    },
};

/**
 * Custom hook to initialize virtual infrastructure detail page business logic.
 * Handles state management, form submission transformation, and save validation.
 *
 * This hook encapsulates the business logic for:
 * - Determining if this is a create action (new vCenter) or edit action
 * - Managing save button disabled state
 * - Storing collector selection reference
 * - Transforming form data for vCenter creation
 * - Validating save operation based on action state
 *
 * @returns {Object} Detail page initialization utilities
 * @returns {string|null} recordId - Current record ID from URL params (null for create)
 * @returns {boolean} isVcenterCreateAction - True if creating new vCenter
 * @returns {Function} dataTableActionErrorHandler - Error handler for actions
 * @returns {boolean} saveButtonDisabled - Whether save button should be disabled
 * @returns {Function} setSaveButtonDisabled - Function to update save button state
 * @returns {React.MutableRefObject} selectionCollectorRef - Ref for selected collector
 * @returns {Function} getConfirmMsg - Function to generate confirmation messages
 * @returns {Function} createSubmitTransform - Transform function for create submissions
 * @returns {Function} isSaveDisabled - Function to determine if save should be disabled
 * @returns {Object} defaultValues - Default form field values
 *
 * @example
 * function VirtualInfrastructureDetail() {
 *   const {
 *     recordId,
 *     isVcenterCreateAction,
 *     createSubmitTransform,
 *     isSaveDisabled,
 *     selectionCollectorRef,
 *     defaultValues
 *   } = useVirtualInfrastructureDetailInit();
 *
 *   // Use values for component rendering and form submission
 * }
 */
export function useVirtualInfrastructureDetailInit() {
    const recordId = useEntityIdParam();
    const isVcenterCreateAction = isEmpty(recordId);
    const dataTableActionErrorHandler = useInfrastructureErrorHandler();
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
    const selectionCollectorRef = useRef(null);

    /**
     * Generate confirmation message for an action based on record type.
     * Returns a React component with the confirmation message.
     *
     * @param {string} action - The action being performed (e.g., 'delete', 'disable', 'enable')
     * @param {string} recordType - The type of the record (e.g., 'vcenter', 'hypervisor')
     * @returns {JSX.Element} Confirmation message component
     */
    const getConfirmMsg = useCallback((action, recordType) => {
        const selectSubTypeSet = getSubtypeSet(recordType);
        return <ConfirmMsg action={action} selectSubTypeSet={selectSubTypeSet} />;
    }, []);

    /**
     * Transform form data for vCenter creation.
     * Adds default values, collector information, and timestamps.
     *
     * @param {Object} dataToSave - Form data to be saved
     * @returns {Object} Transformed data ready for submission
     */
    const createSubmitTransform = useCallback((dataToSave) => {
        if (isEmpty(dataToSave.virtualization?.portNumber)) {
            dataToSave[PORT_FIELD] = DEFAULT_PORT;
        }

        const newDataToSave = extend({}, VCENTER_NEW_DETAIL_OBJECT, dataToSave);
        newDataToSave.virtualization = {
            ...newDataToSave.virtualization,
            collector: VCENTER_NEW_VIRTUALIZATION.virtualization.collector,
            status: VCENTER_NEW_VIRTUALIZATION.virtualization.status,
        };

        newDataToSave[MANUAL_NAME] = dataToSave[VCENTER_HOSTNAME_FIELD];
        newDataToSave[INSERT_TIME] = getSystemTime().valueOf();

        const plcId = selectionCollectorRef.current[ID_MAPPING];
        newDataToSave[DISCOVERY_PLC] = plcId;
        newDataToSave[OWNER_PLC] = plcId;
        newDataToSave[MONITORING_PLC] = plcId;
        newDataToSave[MONITORING_PLC_NAME] = selectionCollectorRef.current[NAME_FIELD];

        return newDataToSave;
    }, []);

    /**
     * Determine if save button should be disabled based on action state.
     * For create actions, considers additional save button disabled state.
     *
     * @param {Object} params
     * @param {boolean} params.saving - Whether save is in progress
     * @param {boolean} params.isActioning - Whether an action is in progress
     * @param {boolean} params.isCreateAction - Whether this is a create action
     * @returns {boolean} True if save should be disabled
     */
    const isSaveDisabled = useCallback(
        ({ saving, isActioning, isCreateAction }) => {
            if (isCreateAction) {
                return saveButtonDisabled || saving || isActioning;
            }
            return saving || isActioning;
        },
        [saveButtonDisabled]
    );

    return {
        recordId,
        isVcenterCreateAction,
        dataTableActionErrorHandler,
        saveButtonDisabled,
        setSaveButtonDisabled,
        selectionCollectorRef,
        getConfirmMsg,
        createSubmitTransform,
        isSaveDisabled,
        defaultValues: DEFAULT_VALUES,
    };
}
